import { Component, Input, OnInit } from '@angular/core'
import { LoaderService } from '@core/services/loader.service'
import { AppService } from '@app/general/services/app.service'
import { Router } from '@angular/router'
import { config } from '@config'
import { Pagination } from '@core/classes/pagination'
import { CashDocumentsService, CashReceiptRead, InvoicesService, IssuerRead } from '@app/generated'
import { finalize, forkJoin, of } from 'rxjs'
import { NotificationService } from '@core/services/notification.service'
import { SignaComponent } from '@app/general/classes/signa-component'
import { saveAs } from 'file-saver'
import { calculateDivision } from '@app/invoices/utils/calculate-division'
import { calculateGrossValueFromNetValue } from '@app/invoices/utils/calculate-gross-value-from-net-value'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { NullableDate, NullableString } from '@core/types/nullable'
import { formatDate } from '@app/general/utils/format-date'
import { ModalService } from '@core/services/modal.service'
import { LanguageService } from '@core/services/language.service'
import { getLanguageCode } from '@app/general/utils/get-language-code'
import { AssignCashReceiptsComponent } from '@app/cash-documents/components/assign-cash-receipts/assign-cash-receipts.component'
import { Permission } from '@app/acl/enums/permissions.enum'
import { ScrollItem } from '@app/orders/components/order-list/order-list.component'
import { setLocalStorageScroll } from '@app/cost-invoices/pages/cost-invoices/cost-invoices.page'
import { LayoutService } from '@core/services/layout.service'

export const CashReceiptsFiltersLocalStorageKey = 'cash-receipts-view-list-filters'
export const TransferFiltersLocalStorageKey = 'transfer-view-list-filters'
export const CashReceiptsListScrollStorageKey = 'cash-receipts-view-list-scroll'
export const TransferListScrollStorageKey = 'transfer-view-list-scroll'

type SortBy = 'created_at' | 'updated_at' | 'deleted_at' | 'document_number'

export type CashReceiptsFiltersValues = {
	[P in keyof FiltersFormScheme]: FiltersFormScheme[P]['value']
}
export type CashReceiptsFiltersAndSearchValues = {
	filters: CashReceiptsFiltersValues | undefined
	sortBy?: SortBy
	sortOrder?: 'asc' | 'desc'
	perPage?: number
	pageNumber?: number
}

interface FiltersFormScheme {
	search: FormControl<NullableString>
	dateFrom: FormControl<NullableDate>
	dateTo: FormControl<NullableDate>
	issuerID: FormControl<NullableString>
}

@Component({
	selector: 'app-cash-receipts',
	templateUrl: './cash-receipts.page.html',
	styleUrl: './cash-receipts.page.scss',
})
export class CashReceiptsPage extends SignaComponent implements OnInit {
	@Input() transfer: boolean = false

	models: CashReceiptRead[] = []
	pagination: Pagination = new Pagination()
	languageCode: string = 'en'
	filtersFormGroup: FormGroup<FiltersFormScheme> = new FormGroup({
		search: new FormControl<NullableString>(null, [Validators.maxLength(128)]),
		dateFrom: new FormControl<NullableDate>(null),
		dateTo: new FormControl<NullableDate>(null),
		issuerID: new FormControl<NullableString>(null),
	})
	issuers: IssuerRead[] = []
	isLoaded: boolean = false
	scrollY: number = 0

	protected readonly textRemoveFilters = $localize`Clear filters`
	protected readonly textSearch = $localize`Search`
	protected readonly textDateOfIssue = $localize`Date of issue`
	protected readonly textFrom = $localize`From`
	protected readonly textTo = $localize`To`
	protected readonly textShowAll = $localize`Show all`
	protected readonly textDelete = $localize`Delete`
	protected readonly textIssuer = $localize`Issuer`
	protected readonly textInvoiceType = $localize`Invoice type`
	protected readonly textDeleteCashReceipt = $localize`Delete cash receipt`
	protected readonly textDeleteTransfer = $localize`Delete incoming transfer`
	protected readonly textDownloadPdf = $localize`Download PDF`
	protected readonly textAssignToInvoice = $localize`Assign to invoice`
	protected readonly textRemoveTheInvoiceAssignment = $localize`Remove the invoice assignment`
	protected readonly textEdit = $localize`Edit`
	private selectedModels: CashReceiptRead[] = []

	constructor(
		private router: Router,
		private loaderService: LoaderService,
		private appService: AppService,
		private cashDocumentsService: CashDocumentsService,
		private notificationService: NotificationService,
		private invoicesService: InvoicesService,
		private modalService: ModalService,
		private languageService: LanguageService,
		private layoutService: LayoutService,
	) {
		super()
		this.loaderService.setCover().enable()
		this.languageCode = getLanguageCode(this.languageService.getSelectedLanguage ?? 'en-US')
	}

	get getSelectedIssuer(): IssuerRead | undefined {
		return this.issuers.find(i => i.id === this.filtersFormGroup.controls.issuerID.value)
	}

	ngOnInit(): void {
		if (this.transfer) {
			this.appService.title = $localize`List of incoming transfers`
		} else {
			this.appService.title = $localize`List of cash receipts`
		}

		const scroll = this.getLocalStorageScroll

		if (scroll?.scrollY) {
			const interval = setInterval(() => {
				if (this.isLoaded) {
					this.layoutService.layoutContainer?.nativeElement.scrollTo({
						top: scroll.scrollY ?? 0,
					})
					clearInterval(interval)
				}
			}, 300)
		}

		this.pagination.currentPageChanges().subscribe(() => {
			if (this.isLoaded) {
				if (this.transfer) {
					sessionStorage.removeItem(TransferListScrollStorageKey)
				} else {
					sessionStorage.removeItem(CashReceiptsListScrollStorageKey)
				}
			}
		})

		this.layoutService.afterScroll.subscribe(event => {
			const target = event!.target as HTMLElement

			if (target) {
				this.scrollY = target.scrollTop
			}
		})

		const filters = this.getLocalStorageFilterAndSort
		if (filters?.filters) {
			this.filtersFormGroup.controls.search.setValue('')
			this.filtersFormGroup.patchValue(filters.filters)
			this.pagination.setPerPage(filters.perPage ?? 25)
		}

		this.pagination.listShouldBeReloaded().subscribe(() => {
			this.fetchData()
		})
	}

	create(): void {
		if (this.transfer) {
			this.loaderService.setContain().enable(async () => {
				await this.router.navigateByUrl(`/${config.modules.cashDocuments.path}/transfer-incoming/create`)
			})
		} else {
			this.loaderService.setContain().enable(async () => {
				await this.router.navigateByUrl(`/${config.modules.cashDocuments.path}/cash-receipts/create`)
			})
		}
	}

	downloadPdf(model: CashReceiptRead, language: string): void {
		this.loaderService.setContain().enable(async () => {
			this.cashDocumentsService
				.getCashReceiptPdf(model.id, language as any, undefined, true, {
					httpHeaderAccept: 'application/pdf',
				})
				.pipe(finalize(() => this.loaderService.disable()))
				.subscribe({
					next: (res: Blob) => {
						let name = $localize`Cash Receipt` + '-' + model.customer_name + '-' + model.issue_date + '.pdf'
						name = name.replace(/\W+/g, '-')
						saveAs(res, name.toLowerCase())
					},
					error: err => {
						this.notificationService.error($localize`Something went wrong.`)
					},
				})
		})
	}

	getGrossValue(model: CashReceiptRead): number {
		return calculateGrossValueFromNetValue({
			netValue: model.net_value,
			taxRate: calculateDivision(model.tax_rate.value, 100),
		})
	}

	confirmFilters(): void {
		this.pagination.reloadList()
	}

	aclCanCashReceiptsRead(): boolean {
		return this.aclService.hasPermission([Permission.CASH_RECEIPTS_READ])
	}

	aclCanCashReceiptsCreate(): boolean {
		return this.aclService.hasPermission([Permission.CASH_RECEIPTS_CREATE])
	}

	aclCanCashReceiptsUpdate(): boolean {
		return this.aclService.hasPermission([Permission.CASH_RECEIPTS_UPDATE])
	}

	aclCanCashReceiptsDelete(): boolean {
		return this.aclService.hasPermission([Permission.CASH_RECEIPTS_DELETE])
	}

	aclCanContractorsRead(): boolean {
		return this.aclService.hasPermission([Permission.CONTRACTORS_READ])
	}

	aclCanReadInvoices(): boolean {
		return this.aclService.hasPermission([Permission.INVOICES_READ])
	}

	async assignToInvoice(model: CashReceiptRead): Promise<void> {
		if (model.is_assigned) {
			this.notificationService.error($localize`The selected document is already assigned to an invoice.`)
		} else {
			const modal = await this.modalService.open(AssignCashReceiptsComponent)
			if (this.transfer) {
				modal.title = $localize`Assign the transfer ${model.document_number} to the invoice.`
				modal.componentInstance.transfer = true
			} else {
				modal.title = $localize`Assign the cash receipt number ${model.document_number} to the invoice.`
			}

			modal.iconName = 'link-alt'
			modal.afterClosed.subscribe(value => {
				if (value) {
					this.cashDocumentsService.assignCashReceiptToInvoice(model.id, { invoice_id: [value] }).subscribe({
						next: () => {
							model.is_assigned = true
							this.notificationService.success($localize`Assigned ${model.document_number} successfully.`)
						},
						error: err => {
							console.error(err)
							this.notificationService.error($localize`Something went wrong.`)
						},
					})
				}
			})
		}
	}

	async unassignFromInvoice(model: CashReceiptRead): Promise<void> {
		const modal = await this.modalService.confirmation({
			title: $localize`Are you sure you want to unassign?`,
			message: $localize`Are you sure you want to unassign the cash receipt?`,
		})

		modal.subscribe(value => {
			if (value) {
				this.cashDocumentsService.unassignCashReceiptFromInvoice(model.id).subscribe({
					next: () => {
						model.is_assigned = false
						this.notificationService.success($localize`Unassigned ${model.document_number} successfully.`)
					},
					error: err => {
						console.error(err)
						this.notificationService.error($localize`Something went wrong.`)
					},
				})
			}
		})
	}

	async update(model: CashReceiptRead): Promise<void> {
		if (this.transfer) {
			setLocalStorageScroll(model.id, this.scrollY, TransferListScrollStorageKey)
			this.loaderService.setContain().enable(async () => {
				await this.router.navigate([`/cash-documents/transfer-incoming/${model.id}/update`])
			})
		} else {
			this.loaderService.setContain().enable(async () => {
				setLocalStorageScroll(model.id, this.scrollY, CashReceiptsListScrollStorageKey)
				await this.router.navigate([`/cash-documents/cash-receipts/${model.id}/update`])
			})
		}
	}

	async delete(model: CashReceiptRead): Promise<void> {
		const modal = await this.modalService.confirmation({
			title: $localize`Delete cash receipt`,
			message: $localize`Are you sure you want to delete?`,
		})

		modal.subscribe({
			next: value => {
				if (value) {
					this.loaderService.setContain().enable(() => {
						this.cashDocumentsService.deleteCashReceipt(model.id).subscribe({
							next: () => this.fetchData(),
							error: err => {
								this.loaderService.disable()
								this.notificationService.error($localize`Something went wrong.`)
							},
						})
					})
				}
			},
		})
	}

	checkboxesChanged(models: CashReceiptRead[]) {
		this.selectedModels = models
	}

	aclCanIssuerRead(): boolean {
		return this.aclService.hasPermission([Permission.ISSUERS_READ])
	}

	async deleteSelected(): Promise<void> {
		if (this.selectedModels.length === 0) {
			this.notificationService.error($localize`You have to select the documents you want to delete.`)
		} else {
			;(
				await this.modalService.confirmation({
					title: $localize`Are you sure you want to delete?`,
					message: $localize`Are you sure you want to delete all selected cash documents? This action cannot be undone.`,
				})
			).subscribe({
				next: value => {
					if (value) {
						this.loaderService.setContain().enable(() => {
							forkJoin(
								this.selectedModels.map(model => this.cashDocumentsService.deleteCashReceipt(model.id)),
							).subscribe({
								next: () => {
									this.pagination.reloadList()
								},
								error: err => {
									console.error(err)
									this.loaderService.disable()
									this.notificationService.error($localize`An error occurred while deleting the cash receipt.`)
								},
							})
						})
					}
				},
			})
		}
	}

	private fetchIssuers() {
		if (this.issuers.length) {
			return of(this.issuers)
		} else {
			return this.invoicesService.getIssuers()
		}
	}

	private fetchData(): void {
		const filters = this.filtersFormGroup.value
		this.setCashReceiptsLocalStorageFiltersAndSort()
		let issueDateTo: string | undefined
		let issueDateFrom: string | undefined
		if (filters.dateFrom instanceof Date) {
			issueDateFrom = formatDate(filters.dateFrom)
		} else if (filters.dateFrom) {
			issueDateFrom = formatDate(new Date(filters.dateFrom))
		}
		if (filters.dateTo instanceof Date) {
			issueDateTo = formatDate(filters.dateTo)
		} else if (filters.dateTo) {
			issueDateTo = formatDate(new Date(filters.dateTo))
		}
		this.loaderService.setContain().enable(async () => {
			forkJoin({
				issuers: this.fetchIssuers(),
				cashReceipts: this.cashDocumentsService.getCashReceipts(
					this.pagination.offset(),
					this.pagination.perPage,
					this.filtersFormGroup.controls.search.value ?? undefined,
					undefined,
					undefined,
					undefined,
					this.filtersFormGroup.controls.issuerID.value ?? undefined,
					filters.dateFrom ? issueDateFrom : undefined,
					filters.dateTo ? issueDateTo : undefined,
					undefined,
					undefined,
					this.transfer ?? false,
				),
			})
				.pipe(finalize(() => this.loaderService.disable()))
				.subscribe({
					next: async response => {
						this.pagination.setTotal(response.cashReceipts.total)
						this.models = response.cashReceipts.items
						this.issuers = response.issuers
						if (!this.isLoaded) {
							const filters = this.getLocalStorageFilterAndSort
							if (filters) {
								this.pagination.setCurrentPage(filters.pageNumber ?? 1)
							}
							this.isLoaded = true
						}
						this.setCashReceiptsLocalStoragePageNumber()
					},
					error: err => {
						console.error(err)
						this.notificationService.error($localize`Something went wrong.`)
					},
				})
		})
	}

	setCashReceiptsLocalStorageFiltersAndSort(): void {
		const filters = this.getLocalStorageFilterAndSort
		sessionStorage.setItem(
			this.transfer ? TransferFiltersLocalStorageKey : CashReceiptsFiltersLocalStorageKey,
			JSON.stringify({
				filters: this.filtersFormGroup.value ?? null,
				perPage: this.pagination.perPage,
				pageNumber: filters?.pageNumber ?? 1,
			}),
		)
	}

	setCashReceiptsLocalStoragePageNumber(): void {
		sessionStorage.setItem(
			this.transfer ? TransferFiltersLocalStorageKey : CashReceiptsFiltersLocalStorageKey,
			JSON.stringify({
				filters: this.filtersFormGroup.value ?? null,
				perPage: this.pagination.perPage,
				pageNumber: this.pagination.currentPage,
			}),
		)
	}

	get getLocalStorageFilterAndSort(): CashReceiptsFiltersAndSearchValues | undefined {
		const storedFilters = sessionStorage.getItem(
			this.transfer ? TransferFiltersLocalStorageKey : CashReceiptsFiltersLocalStorageKey,
		)

		if (!storedFilters) {
			return undefined
		}

		return JSON.parse(storedFilters)
	}

	get getLocalStorageScroll(): ScrollItem | undefined {
		const storedFilters = sessionStorage.getItem(
			this.transfer ? TransferListScrollStorageKey : CashReceiptsListScrollStorageKey,
		)

		if (!storedFilters) {
			return undefined
		}

		return JSON.parse(storedFilters)
	}

	clearFilters() {
		sessionStorage.removeItem(this.transfer ? TransferListScrollStorageKey : CashReceiptsListScrollStorageKey)
	}

	goToInvoice(id: string) {
		this.loaderService.setContain().enable(() => {
			this.router.navigate([`/invoices/${id}`])
		})
	}
}
